import { useMutation } from "@apollo/client"
import { FC, useEffect, useRef, useState } from "react"
import { toast } from "react-toastify"
import { Col, Input, Modal, ModalBody, ModalHeader, Row } from "reactstrap"
import { moneyFormatter } from "../../utils/helper"
import CustomButton from "../layout/CustomButton"
import {
  FLAG_TEMP_BILLING_INVOICES,
  ITempBillingReconcileResult,
  ITempBillingReconciliationInvoice,
  TEMP_BILLING_REASON_LABEL,
  TEMP_BILLING_SKIP_REASON_TEXT,
  TempBillingAcceptedReason,
  TempBillingFlagStatus,
} from "./gql"

// kan-112. The two per-invoice decisions an accountant makes when an invoice
// cannot simply be Cleared:
//
//   Accepted — an EXPLAINED reconciling item (chargeback, People 2.0 fee, credit
//              memo, rounding). The difference is understood and approved, so a
//              reason code is required. Only an orphan or a mismatch can be
//              Accepted: a matched invoice has nothing to explain and is Cleared.
//   Suspense — an UNEXPLAINED difference parked so the payment can proceed. The
//              note is what the executive reads when they come back to it, so it
//              is required and is the whole record of why it was parked.
//
// Both settle the invoice under this payment. The modal acts on ONE invoice at a
// time on purpose: each flag carries its own reason or note, and a bulk flag
// would stamp one explanation onto differences that were never looked at.

interface IFlagInvoiceModalProps {
  isOpen: boolean;
  toggle: () => void;
  paymentMetaId: string;
  invoice: ITempBillingReconciliationInvoice | null;
  refetch: Function;
}

/** Mirrors the server's MaxLength on TempBillingFlagInput.note. */
const NOTE_MAX = 2000

const REASON_KEYS = Object.keys(TEMP_BILLING_REASON_LABEL) as Array<TempBillingAcceptedReason>

const RADIO_LABEL_STYLE = { cursor: 'pointer' } as const
const RADIO_STYLE = { margin: '0 8px 0 0', flex: '0 0 auto' } as const

/** Re-flagging keeps the current decision; otherwise the likeliest one. */
const initialStatus = (invoice: ITempBillingReconciliationInvoice | null): TempBillingFlagStatus => {
  const current = invoice?.status
  if (current === 'ACCEPTED' || current === 'SUSPENSE') return current
  return invoice?.acceptable ? 'ACCEPTED' : 'SUSPENSE'
}

const skipMessage = (result?: ITempBillingReconcileResult | null): string =>
  (result?.skipped || [])
    .map((skip) =>
      `${skip?.invoiceNumber}: ${TEMP_BILLING_SKIP_REASON_TEXT[skip?.reason] || skip?.reason}`
    )
    .join(' · ')

const FlagInvoiceModal: FC<IFlagInvoiceModalProps> = ({
  isOpen,
  toggle,
  paymentMetaId,
  invoice,
  refetch,
}) => {
  const [status, setStatus] = useState<TempBillingFlagStatus>('SUSPENSE')
  const [reason, setReason] = useState<TempBillingAcceptedReason | ''>('')
  const [note, setNote] = useState<string>('')
  const [errorMessage, setErrorMessage] = useState<string>('')

  // The status the mutation was sent with, for the toast wording. A ref, not
  // state: Apollo captures the options (and so onCompleted) when the mutation
  // fires, before a state update could re-render into them.
  const sentStatus = useRef<TempBillingFlagStatus>('SUSPENSE')

  const [flagInvoices, { loading }] = useMutation(FLAG_TEMP_BILLING_INVOICES, {
    onCompleted: (res) => {
      const result: ITempBillingReconcileResult | undefined = res?.flagTempBillingInvoices
      const verb = sentStatus.current === 'ACCEPTED' ? 'accepted' : 'put in suspense'
      if (result?.affectedCount) {
        toast.success(`${result?.affectedCount} invoice(s) ${verb}`)
      }
      if (result?.skipped?.length) {
        toast.warning(`${result?.skipped?.length} skipped — ${skipMessage(result)}`)
      }
      if (!result?.affectedCount && !result?.skipped?.length) {
        toast.info('Nothing to do')
      }
      refetch()
      toggle()
    },
    // Validation failures (missing reason / note) arrive as a plain message —
    // show it where the executive is looking, not only as a toast.
    onError: (error) => {
      setErrorMessage(error?.message || "something went wrong")
      toast.error(error?.message || "something went wrong")
    },
  })

  useEffect(() => {
    setStatus(initialStatus(invoice))
    setReason((invoice?.flagReason as TempBillingAcceptedReason) || '')
    setNote(invoice?.flagNote || '')
    setErrorMessage('')
  }, [isOpen, invoice])

  const acceptable = !!invoice?.acceptable
  const trimmedNote = note.trim()
  const isAccepted = status === 'ACCEPTED'

  // Accepted needs a reason code; "Other" is not an explanation on its own, so
  // it needs the note to say what the item actually is. Suspense needs the note.
  const noteRequired = !isAccepted || reason === 'OTHER'
  const valid = isAccepted
    ? acceptable && !!reason && (!noteRequired || !!trimmedNote)
    : !!trimmedNote

  const onSave = async () => {
    if (!invoice || !valid) return
    setErrorMessage('')
    sentStatus.current = status
    await flagInvoices({
      variables: {
        input: {
          paymentMetaId,
          invoiceNumbers: [invoice.invoiceKey],
          status,
          // The server refuses a reason on a Suspense item — it would read as an
          // explanation that was never given.
          ...(isAccepted ? { reason } : {}),
          ...(trimmedNote ? { note: trimmedNote } : {}),
        }
      }
    })
  }

  const reflagging = invoice?.status === 'ACCEPTED' || invoice?.status === 'SUSPENSE'

  return (
    <Modal isOpen={isOpen} toggle={toggle} className="revampDialog revampDialogWidth">
      <ModalHeader toggle={toggle}>
        {acceptable ? 'Flag' : 'Put in Suspense'} {invoice?.invoiceNumber || ''}
      </ModalHeader>
      <ModalBody className="p-4">
        <Col>
          <p className="text-muted mb-3">
            {invoice?.employeeName || '--'}
            {invoice?.companyName ? ` · ${invoice?.companyName}` : ''}
          </p>

          {/* The three numbers the decision is about, laid out the way the
              invoice row reads: what we billed, what People 2.0 paid, the gap. */}
          <Row className="mb-2">
            <Col sm="4">
              <small className="text-muted d-block">Weekly Activity (billed)</small>
              <span className="font-weight-bold">
                {moneyFormatter(invoice?.weeklyActivityTotal || 0)}
              </span>
            </Col>
            <Col sm="4">
              <small className="text-muted d-block">Profit Summary (paid)</small>
              <span className="font-weight-bold">
                {moneyFormatter(invoice?.profitSummaryTotal || 0)}
              </span>
            </Col>
            <Col sm="4">
              <small className="text-muted d-block">Difference</small>
              <span className={`font-weight-bold${invoice?.matched ? '' : ' text-danger'}`}>
                {moneyFormatter(invoice?.difference || 0)}
              </span>
            </Col>
          </Row>

          {!!invoice?.orphan && (
            <p className="text-danger mb-3">
              No weekly activity rows carry this invoice number in any import.
            </p>
          )}

          <p className="font-weight-bold mt-3 mb-2">Status <span className="text-danger">*</span></p>
          {/* A matched invoice has no difference to explain, so Accepted is not
              offered at all rather than shown greyed out — the only decision left
              is whether to hold it in suspense for a reason the numbers cannot
              show (wrong client, wrong period, duplicate). */}
          {!acceptable && (
            <p className="text-muted mb-2">
              This invoice matches its weekly activity, so it is cleared or held in
              suspense — never accepted.
            </p>
          )}
          {/* Native radios in a flex label, not Bootstrap's .form-check: that class
              positions the input absolutely with a fixed top margin, which sits it
              above the baseline of a label that mixes bold and muted text. */}
          {acceptable && (
            <div className="mb-2">
              <label className="d-flex align-items-center m-0" style={RADIO_LABEL_STYLE}>
                <input
                  type="radio"
                  name="tbr-flag-status"
                  style={RADIO_STYLE}
                  checked={isAccepted}
                  onChange={() => {
                    setStatus('ACCEPTED')
                    setErrorMessage('')
                  }}
                />
                <span>
                  <span className="font-weight-bold">Accepted</span>
                  <span className="text-muted">
                    {' '}— Explained difference — chargeback, fee, credit memo, rounding
                  </span>
                </span>
              </label>
            </div>
          )}
          <div className="mb-3">
            <label className="d-flex align-items-center m-0" style={RADIO_LABEL_STYLE}>
              <input
                type="radio"
                name="tbr-flag-status"
                style={RADIO_STYLE}
                checked={!isAccepted}
                onChange={() => {
                  setStatus('SUSPENSE')
                  setErrorMessage('')
                }}
              />
              <span>
                <span className="font-weight-bold">Suspense</span>
                <span className="text-muted"> — Unexplained — proceed now, investigate later</span>
              </span>
            </label>
          </div>

          {isAccepted && (
            <div className="mb-3">
              <p className="font-weight-bold mb-2">Reason <span className="text-danger">*</span></p>
              <Input
                type="select"
                value={reason}
                onChange={({ target: { value } }) => {
                  setReason(value as TempBillingAcceptedReason | '')
                  setErrorMessage('')
                }}
              >
                <option value="">Select a reason</option>
                {REASON_KEYS.map((key) => (
                  <option key={key} value={key}>{TEMP_BILLING_REASON_LABEL[key]}</option>
                ))}
              </Input>
            </div>
          )}

          <div>
            <p className="font-weight-bold mb-2">
              Note {noteRequired && <span className="text-danger">*</span>}
            </p>
            <Input
              type="textarea"
              rows={4}
              maxLength={NOTE_MAX}
              value={note}
              placeholder={isAccepted
                ? 'Optional — e.g. credit memo number or chargeback reference'
                : ''}
              onChange={({ target: { value } }) => {
                setNote(value)
                setErrorMessage('')
              }}
            />
            <small className="text-muted">
              {isAccepted
                ? reason === 'OTHER'
                  ? 'Say what this reconciling item is — "Other" is not an explanation on its own.'
                  : 'Optional supporting detail for the reconciling item.'
                : 'What needs to be investigated — this is what you will read when you come back to it.'}
            </small>
          </div>
        </Col>

        {!!errorMessage && (
          <Col className="mt-3">
            <p className="text-danger m-0">{errorMessage}</p>
          </Col>
        )}

        <div className="mt-5 d-flex w-40 align-self ml-auto">
          <CustomButton
            className="ml-auto"
            disabled={loading || !invoice || !valid}
            loading={loading}
            buttonClick={onSave}
            buttonText={reflagging ? 'Update Flag' : isAccepted ? 'Accept Invoice' : 'Put in Suspense'}
          />
        </div>
      </ModalBody>
    </Modal>
  )
}

export default FlagInvoiceModal
